import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {MatDialogRef} from "@angular/material/dialog";
import {
  AdditionalFieldsAddDialogComponent
} from "../additional-fields-add-dialog/additional-fields-add-dialog.component";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {Observable, of, ReplaySubject} from "rxjs";
import {UserPermissions} from "../../../shared/interface/user-permissions";
import {AuthService} from "../../../shared/services/auth.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {FormHelperService} from "../../../shared/services/form-helper.service";
import {HttpErrorResponse} from "@angular/common/http";
import {AdditionalFieldsService} from "../../../shared/services/additional-fields.service";
import {AdditionalField} from "../../../shared/interface/additionalField";
import {takeUntil, tap} from "rxjs/operators";

@Component({
  selector: 'app-additional-fields-add',
  templateUrl: './additional-fields-add.component.html',
  styleUrls: ['./additional-fields-add.component.scss']
})
export class AdditionalFieldsAddComponent implements OnInit, OnDestroy {
  @Input() dialogRef?: MatDialogRef<AdditionalFieldsAddDialogComponent>;
  @Input() additionalField: Observable<AdditionalField>;
  @Output() closeModification = new EventEmitter<void>();
  @Input() additionalFieldObject?: AdditionalField;

  additionalFieldForm: FormGroup;
  processingRequest = false;
  errors: any = {};
  userPermissions: ReplaySubject<UserPermissions>;
  fieldNameControl: FormControl;
  fieldTypeControl: FormControl;
  fieldActiveControl: FormControl;

  private destroyed$ = new ReplaySubject<boolean>(1);
  private initialized: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private fb: FormBuilder,
    private auth: AuthService,
    private snack: MatSnackBar,
    private additionalFieldsService: AdditionalFieldsService,
    private formHelper: FormHelperService,
  ) {
    this.userPermissions = this.auth.getUserPermissions();

    this.additionalFieldForm = fb.group({
      name: this.fieldNameControl = fb.control('', [Validators.required]),
      fieldType: this.fieldTypeControl = fb.control('', [Validators.required]),
      active: this.fieldActiveControl = fb.control(''),
    });
  }

  ngOnInit(): void {
    if (this.additionalField) {
      this.additionalField
        .pipe(takeUntil(this.destroyed$))
        .subscribe((additionalFieldObject: AdditionalField) => {
          this.additionalFieldObject = additionalFieldObject;
          this.initialize(additionalFieldObject);
          this.fieldNameControl.disable();
          this.fieldTypeControl.disable();
        });
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  initialize(additionalFieldObject: AdditionalField): void {
    of(additionalFieldObject)
      .pipe(
        tap(() => {

          this.additionalFieldForm.reset({
            name: additionalFieldObject.fieldName,
            fieldType: additionalFieldObject.fieldType,
            active: additionalFieldObject.active,
          });
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        this.initialized.next(true);
      });
  }

  sendForm(): void {
    this.processingRequest = true;

    if (!this.additionalFieldObject) {
      this.additionalFieldsService.addAdditionalField(this.additionalFieldForm.getRawValue())
        .subscribe(() => {
          this.snack.open('Pomyślnie dodano dodatkowe pole', '', {
            duration: 2000,
          });
          this.processingRequest = false;
          this.closeModification.emit();
          this.dialogRef.close();
        }, (error: HttpErrorResponse) => {
          this.processingRequest = false;
          this.snack.open('Podczas dodawania wystąpił błąd', error.status.toString(), {
            duration: 2000,
          });
          if (error.status === 400) {
            this.errors = error.error.error.list;
            if (this.errors.create_additional_field_form) {
              this.additionalFieldForm.get('name')?.setErrors({
                backendError: this.errors.create_additional_field_form
              });
              this.additionalFieldForm.get('name')?.markAsTouched();
            }
          }
        })
      ;
    } else {
      this.additionalFieldsService.updateActive(this.additionalFieldForm.getRawValue(), this.additionalFieldObject.uuid)
        .subscribe(() => {
          this.snack.open('Pomyślnie zaktualizowano dodatkowe pole', '', {
            duration: 2000,
          });
          this.processingRequest = false;
          this.closeModification.emit();
        }, (error: HttpErrorResponse) => {
          this.processingRequest = false;
          this.snack.open('Podczas aktualizowania wystąpił błąd', error.status.toString(), {
            duration: 2000,
          });
          if (error.status === 400) {
            this.errors = error.error.error.list;
            this.formHelper.setBackendErrorState(this.additionalFieldForm, this.errors);
          }
        })
      ;
    }
  }
  cancel(): void {
    if (this.additionalField){
      this.closeModification.emit();
      return;
    }
    this.dialogRef.close();
  }

}
